"use client";

import SchoolIcon from "@svgs/school.svg";
import { Check } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

// 조직도 노드 인터페이스
export interface OrgNode {
  id: string;
  name: string;
  children?: OrgNode[];
}

type PositionType = "start" | "center" | "end";

// 기본 스타일
const BASE_STYLE =
  "relative flex justify-center items-center whitespace-pre text-center px-2 md:px-3 h-9 md:h-10 lg:h-14 rounded-[12px] md:rounded-[14px] lg:rounded-[18px] outline-2 md:outline-3 lg:outline-5 cursor-pointer transition-all duration-100 transform hover:scale-105";

// 레벨별 색상 스타일
const LEVEL_COLOR_STYLES = {
  0: "bg-gradient-to-r from-[#08A55E] to-[#006CAF] outline-blue-400/15 text-white", // 루트 노드 (RISE 사업단장) - 연두/청록 그라데이션
  1: "bg-gradient-to-t from-[#34247B] to-[#424242] outline-purple-400/15 text-white", // 1레벨 노드 (전략기획부단장, 실행운영부단장, 위원회들) - 보라색 그라데이션
  2: "bg-gradient-to-r from-[#08A55E] to-[#006CAF] outline-blue-400/15 text-white", // 2레벨 노드 (행정부서, 핵심인재양성 등) - 사업단장과 동일
  3: "bg-white border-2 border-[#53488C] outline-[#53488C]/15 text-[#53488C]", // 3레벨 노드 (전략기획부, 반도체 등) - 하얀 배경 + 보라색 테두리
  4: "bg-white border-2 border-[#53488C] outline-[#53488C]/15 text-[#53488C]", // 4레벨 노드
  default:
    "bg-gradient-to-t from-[#34247B] to-[#424242] outline-purple-400/15 text-white", // 기본 색상
};

// 레벨별 선택된 노드 색상 스타일
const SELECTED_LEVEL_COLOR_STYLES = {
  0: "bg-gradient-to-r from-[#067A47] to-[#005A8F] outline-blue-400/40 text-white", // 선택된 루트 노드
  1: "bg-gradient-to-t from-[#2D2552] to-[#3A3A3A] outline-purple-400/40 text-white", // 선택된 1레벨 노드
  2: "bg-gradient-to-r from-[#067A47] to-[#005A8F] outline-blue-400/40 text-white", // 선택된 2레벨 노드 - 사업단장과 동일
  3: "bg-[#53488C] border-2 border-[#53488C] outline-[#53488C]/40 text-white", // 선택된 3레벨 노드
  4: "bg-[#53488C] border-2 border-[#53488C] outline-[#53488C]/40 text-white", // 선택된 4레벨 노드
  default:
    "bg-gradient-to-t from-[#2D2552] to-[#3A3A3A] outline-purple-400/40 text-white", // 선택된 기본 색상
};

// 레벨별 너비
const LEVEL_WIDTHS = {
  0: "w-36 md:w-40 lg:w-60",
  1: "w-16 md:w-18 lg:w-28",
  2: "w-18 md:w-20 lg:w-32",
  3: "w-16 md:w-18 lg:w-28",
  4: "w-16 md:w-18 lg:w-28",
  default: "w-24 md:w-28",
};

// 조직도 데이터
export const orgData: OrgNode = {
  id: "root",
  name: "RISE 사업단장",
  children: [
    // Level 1: 전략기획부단장, 실행운영부단장, 위원회들
    {
      id: "전략기획부단장",
      name: "전략기획부단장",
    },
    {
      id: "실행운영부단장",
      name: "실행운영부단장",
    },
    {
      id: "실무추진위원회",
      name: "실무추진위원회",
    },
    {
      id: "자체평가위원회",
      name: "자체평가위원회",
    },
    {
      id: "성과관리위원회",
      name: "성과관리위원회",
    },
    // Level 2: 행정부서, 핵심인재양성 등
    {
      id: "level2-group",
      name: "Level 2 Group", // 이건 표시되지 않음
      children: [
        {
          id: "administration",
          name: "행정부서",
          children: [
            { id: "strategic-planning", name: "전략기획부" },
            { id: "future-talent", name: "미래인재부" },
            { id: "industry-academia", name: "지산학연협력부" },
          ],
        },
        {
          id: "core-talent",
          name: "핵심인재양성",
          children: [
            { id: "core-semiconductor", name: "반도체" },
            { id: "core-battery", name: "이차전지" },
            { id: "core-bio", name: "바이오" },
          ],
        },
        {
          id: "regional-specialization",
          name: "시군특화형",
          children: [
            { id: "regional-ai", name: "AI모빌리티" },
            { id: "regional-bioai", name: "바이오-AI셀뱅크" },
            { id: "regional-agri", name: "첨단농업 \n (Agri-Life)" },
            { id: "regional-choongju", name: "충주형, 증평형 \n 풀뿌리" },
            { id: "regional-cyber", name: "사이버 보안" },
          ],
        },
        {
          id: "cluster",
          name: "클러스터",
          children: [
            { id: "cluster-osong", name: "오송철도" },
            { id: "cluster-circular", name: "순환경제" },
            { id: "cluster-healthcare", name: "보건의료" },
          ],
        },
        {
          id: "joint-research",
          name: "공동연구센터",
          children: [
            { id: "joint-bio", name: "첨단바이오" },
            { id: "joint-battery", name: "이차전지" },
            { id: "joint-semiconductor", name: "반도체" },
            { id: "joint-ai", name: "AI모빌리티" },
            { id: "joint-robot", name: "첨단로봇" },
            { id: "joint-axcenter", name: "AX센터" },
          ],
        },
        {
          id: "regional-issues",
          name: "지역현안해결",
          children: [
            { id: "startup", name: "창업지원센터" },
            { id: "lifelong", name: "평생교육 \n (에이스 브릿지)" },
            { id: "lifelong-healthcare", name: "평생교육 \n (전문간호)" },
            { id: "k-student", name: "K-유학생" },
            { id: "k-student-startup", name: "K-유학생 창업" },
            { id: "living-research", name: "생활연구소" },
            { id: "neulbom", name: "늘봄교육" },
            { id: "synchrotron", name: "방사광가속기" },
          ],
        },
        {
          id: "shared-university",
          name: "공유대학",
          children: [
            { id: "shared-governance", name: "거버넌스" },
            { id: "shared-academic", name: "학사운영" },
            { id: "shared-enterprise", name: "기업지원" },
          ],
        },
      ],
    },
  ],
};

/**
 * 노드 경로를 찾는 함수
 *
 * @param nodes 조직도 노드 배열
 * @param targetId 대상 노드 ID
 * @param path 현재까지의 경로
 * @returns 노드 경로 또는 null
 */
export const findNodePath = (
  nodes: OrgNode[],
  targetId: string,
  path: OrgNode[] = []
): OrgNode[] | null => {
  for (const node of nodes) {
    const currentPath = [...path, node];

    if (node.id === targetId) {
      return currentPath;
    }

    if (node.children) {
      const result = findNodePath(node.children, targetId, currentPath);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

// 조직도 노드 컴포넌트
const OrgNode: React.FC<{
  node: OrgNode;
  level: number;
  isExpanded: boolean;
  onExpand?: (id: string) => void;
  onToggle: (id: string) => void;
  chartWidth?: number;
  expandedNodeId?: string | null;
  position?: PositionType;
  sub?: string;
  selectedDepartment?: string | null;
  orgData?: OrgNode;
}> = ({
  node,
  level,
  isExpanded,
  onExpand,
  onToggle,
  chartWidth = 0,
  expandedNodeId = null,
  position = "start",
  sub,
  selectedDepartment,
  orgData,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const nodeRef = useRef<HTMLDivElement>(null);

  // 선택된 노드인지 확인
  const isSelected = selectedDepartment === node.id;

  // 레벨별 색상 스타일 가져오기
  const getNodeColorStyle = (level: number) => {
    return (
      LEVEL_COLOR_STYLES[level as keyof typeof LEVEL_COLOR_STYLES] ||
      LEVEL_COLOR_STYLES.default
    );
  };

  // 선택된 노드의 색상 스타일 가져오기
  const getSelectedNodeColorStyle = (level: number) => {
    return (
      SELECTED_LEVEL_COLOR_STYLES[
        level as keyof typeof SELECTED_LEVEL_COLOR_STYLES
      ] || SELECTED_LEVEL_COLOR_STYLES.default
    );
  };

  // 레벨별 너비 가져오기
  const getNodeWidth = (level: number) => {
    return (
      LEVEL_WIDTHS[level as keyof typeof LEVEL_WIDTHS] || LEVEL_WIDTHS.default
    );
  };

  // 루트 노드 (레벨 0)
  if (level === 0 && !sub) {
    return (
      <div className="flex flex-col items-center">
        {/* 루트 노드는 사용자 정의 스타일을 적용할 수 있도록 유지 */}
        <div
          className="bg-blue-400/10 p-1 md:p-1.5 rounded-full -mb-2 md:-mb-3 transition-all duration-100 transform hover:scale-105"
          onClick={() => onToggle(node.id)}
        >
          <div
            className={`relative flex justify-center items-center whitespace-pre text-center px-2 md:px-3 h-10 md:h-12 lg:h-14 rounded-[16px] md:rounded-[18px] outline-2 md:outline-3 lg:outline-4 cursor-pointer ${getNodeColorStyle(
              level
            )} ${getNodeWidth(level)} rounded-full`}
          >
            <SchoolIcon className="absolute right-2 md:right-2.5 lg:right-3 -bottom-0.5 lg:-bottom-1 w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 text-white/10" />
            <div className="font-bold text-xs md:text-sm lg:text-xl">
              {node.name}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 하위 노드 (sub prop을 통한 특수 렌더링) - Level 1 nodes
  if (level === 0 && sub && orgData) {
    // orgData에서 실제 노드 찾기
    const actualNode = orgData.children?.find((child) => child.id === sub);
    const hasActualChildren =
      actualNode?.children && actualNode.children.length > 0;

    return (
      <div
        className="flex flex-col items-center"
        style={{ position: "relative" }}
      >
        <div
          className={`relative flex justify-center items-center whitespace-pre text-center px-2 md:px-3 w-28 md:w-32 lg:w-48 h-9 md:h-11 lg:h-14 rounded-[18px] md:rounded-[22px] shadow outline-2 md:outline-3 lg:outline-4 cursor-pointer transition-all duration-100 transform hover:scale-105 ${getNodeColorStyle(
            1
          )}`}
          onClick={() => {
            onToggle(sub);
            if (hasActualChildren && onExpand) {
              onExpand(sub);
            }
          }}
        >
          <div className="font-bold text-[10px] md:text-xs lg:text-lg text-white">
            {sub}
          </div>
          {hasActualChildren && (
            <div
              className={`absolute -bottom-1 md:-bottom-1.25 lg:-bottom-2 w-2 h-2 md:w-2.5 md:h-2.5 lg:w-4 lg:h-4 duration-100 ${getNodeColorStyle(
                1
              )} rotate-45`}
            />
          )}
        </div>

        {/* 자식 노드 표시 - Level 2 children */}
        {hasActualChildren && actualNode && expandedNodeId === sub && (
          <div
            className="absolute top-full flex flex-col items-center mt-3 lg:mt-4"
            style={{
              zIndex: 10,
              width: "300px",
            }}
          >
            <div className="w-px h-8 lg:h-12 bg-gray-400"></div>
            <div className="flex justify-center w-full">
              {actualNode.children && (
                <div className="relative p-4 lg:p-5 flex flex-col items-center gap-3 lg:gap-4 max-w-4xl border-2 lg:border-3 border-dashed border-gray-300 rounded-2xl">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-gray-400 outline-2 outline-white" />
                  {actualNode.children?.map((child) => {
                    return (
                      <OrgNode
                        key={child.id}
                        node={child}
                        level={2}
                        isExpanded={expandedNodeId === child.id}
                        onToggle={onToggle}
                        onExpand={onExpand}
                        chartWidth={chartWidth}
                        expandedNodeId={expandedNodeId}
                        selectedDepartment={selectedDepartment}
                        orgData={orgData}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // 레벨 1 노드 - Currently unused, kept for future extension
  if (level === 1) {
    return (
      <div
        className="flex flex-col items-center"
        ref={nodeRef}
        style={{ position: "relative" }}
      >
        <div
          className={`
            ${BASE_STYLE}
            ${
              isSelected
                ? getSelectedNodeColorStyle(level)
                : getNodeColorStyle(level)
            } ${getNodeWidth(level)}
          `}
          onClick={() => {
            onToggle(node.id);
            if (hasChildren && onExpand) {
              onExpand(node.id);
            }
          }}
        >
          <div className="font-semibold text-[9px] md:text-[10px] lg:text-sm">
            {node.name}
          </div>
          {hasChildren && (
            <div
              className={`absolute -bottom-1 md:-bottom-1.25 lg:-bottom-2 w-2 h-2 md:w-2.5 md:h-2.5 lg:w-4 lg:h-4 duration-100 ${
                isSelected
                  ? getSelectedNodeColorStyle(level)
                  : getNodeColorStyle(level)
              } rotate-45`}
            />
          )}
        </div>
      </div>
    );
  }

  // 레벨 2 노드 (행정부서, 핵심인재양성, etc.) - 수평으로 표시, Level 3 children 표시
  if (level === 2) {
    return (
      <div
        className="flex flex-col items-center"
        ref={nodeRef}
        style={{ position: "relative" }}
      >
        <div
          className={`
            ${BASE_STYLE}
            ${
              isSelected
                ? getSelectedNodeColorStyle(level)
                : getNodeColorStyle(level)
            } ${getNodeWidth(level)}
          `}
          onClick={() => {
            onToggle(node.id);
            if (hasChildren && onExpand) {
              onExpand(node.id);
            }
          }}
        >
          <div className="font-semibold text-[9px] md:text-[10px] lg:text-sm">
            {node.name}
          </div>
          {hasChildren && (
            <div
              className={`absolute -bottom-1 md:-bottom-1.25 lg:-bottom-2 w-2 h-2 md:w-2.5 md:h-2.5 lg:w-4 lg:h-4 duration-100 ${
                isSelected
                  ? getSelectedNodeColorStyle(level)
                  : getNodeColorStyle(level)
              } rotate-45`}
            />
          )}
        </div>

        {/* Level 3 자식 노드 표시 - 항상 표시 */}
        {hasChildren && (
          <div
            className="absolute top-full flex flex-col items-center mt-1.25 lg:mt-2 pointer-events-none"
            style={{
              zIndex: 10,
              width: `${chartWidth}px`,
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <div className="w-px h-8 lg:h-12 bg-gray-400"></div>
            <div className="flex justify-center w-full pointer-events-none">
              {node.children && (
                <div className="relative p-4 lg:p-5 flex flex-col items-center gap-3 lg:gap-4 max-w-4xl border-2 lg:border-3 border-dashed border-gray-300 rounded-2xl pointer-events-none">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-gray-400 outline-2 outline-white pointer-events-none" />
                  {node.children?.map((child) => {
                    return (
                      <div key={child.id} className="pointer-events-auto">
                        <OrgNode
                          node={child}
                          level={3}
                          isExpanded={isExpanded}
                          onToggle={onToggle}
                          chartWidth={chartWidth}
                          expandedNodeId={expandedNodeId}
                          selectedDepartment={selectedDepartment}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // 레벨 3+ 노드 (전략기획부, 반도체, AI모빌리티, etc.) - 단순 표시
  return (
    <div
      className="flex flex-col items-center"
      style={{ position: "relative" }}
    >
      <div
        className={`
          ${BASE_STYLE}
          ${
            isSelected
              ? getSelectedNodeColorStyle(level)
              : getNodeColorStyle(level)
          } ${getNodeWidth(level)}
        `}
        onClick={() => onToggle(node.id)}
      >
        <div className="font-semibold text-[10px] lg:text-sm">{node.name}</div>
      </div>
    </div>
  );
};

// 인터랙티브 조직도 컴포넌트
const InteractiveOrgChart: React.FC<{
  onDepartmentSelect: (id: string | null) => void;
  selectedDepartment: string | null;
}> = ({ onDepartmentSelect, selectedDepartment }) => {
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(
    "administration"
  );
  const [expandedMobileSection, setExpandedMobileSection] = useState<
    string | null
  >(null);
  const [chartWidth, setChartWidth] = useState(0);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chartRef.current) {
      setChartWidth(chartRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggle = (id: string) => {
    // 부서 선택 상태 업데이트
    onDepartmentSelect(id);
  };

  const handleExpand = (id: string) => {
    setExpandedNodeId(id);
  };

  // 레벨 2 노드들을 렌더링
  const renderLevel2Nodes = () => {
    // level2-group의 children을 가져옴
    const level2Group = orgData.children?.find(
      (child) => child.id === "level2-group"
    );
    const level2Nodes = level2Group?.children || [];

    // 작은 화면에서의 특별한 배치:
    // 1,2,3,7번째 노드 (인덱스 0,1,2,6)를 위쪽에 배치
    // 4,5,6번째 노드 (인덱스 3,4,5)를 아래쪽에 배치하고 5,4,6 순서로 정렬
    const topNodes = [
      level2Nodes[0], // 1번째
      level2Nodes[1], // 2번째
      level2Nodes[2], // 3번째
      level2Nodes[6], // 7번째
    ].filter(Boolean);

    const bottomNodes = [
      level2Nodes[4], // 5번째
      level2Nodes[3], // 4번째
      level2Nodes[5], // 6번째
    ].filter(Boolean);

    return (
      <>
        {/* 기본 배치 (큰 화면) - 1400px 이상에서만 표시 */}
        <div className="hidden [@media(min-width:1460px)]:flex flex-wrap justify-center gap-8 lg:gap-12">
          {level2Nodes.map((node) => {
            return (
              <OrgNode
                key={node.id}
                node={node}
                level={2}
                isExpanded={expandedNodeId === node.id}
                onToggle={handleToggle}
                onExpand={handleExpand}
                chartWidth={chartWidth}
                expandedNodeId={expandedNodeId}
                selectedDepartment={selectedDepartment}
                orgData={orgData}
              />
            );
          })}
        </div>

        {/* 작은 화면용 배치 - 1400px 미만에서만 표시 */}
        <div className="hidden flex-col items-center [@media(max-width:1459px)]:flex w-full">
          {/* 위쪽 노드들 */}
          <div className="flex flex-wrap justify-center gap-3 lg:gap-4 w-full">
            {topNodes.map((node) => {
              return (
                <OrgNode
                  key={node.id}
                  node={node}
                  level={2}
                  isExpanded={expandedNodeId === node.id}
                  onToggle={handleToggle}
                  onExpand={handleExpand}
                  chartWidth={chartWidth}
                  expandedNodeId={expandedNodeId}
                  selectedDepartment={selectedDepartment}
                  orgData={orgData}
                />
              );
            })}
          </div>

          {/* 아래쪽 노드들 */}
          <div className="flex flex-wrap justify-center gap-3 lg:gap-4 w-full mt-4 lg:mt-6">
            {bottomNodes.map((node) => {
              return (
                <OrgNode
                  key={node.id}
                  node={node}
                  level={2}
                  isExpanded={expandedNodeId === node.id}
                  onToggle={handleToggle}
                  onExpand={handleExpand}
                  chartWidth={chartWidth}
                  expandedNodeId={expandedNodeId}
                  selectedDepartment={selectedDepartment}
                  orgData={orgData}
                />
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const toggleMobileSection = (sectionId: string) => {
    setExpandedMobileSection(
      expandedMobileSection === sectionId ? null : sectionId
    );
  };

  // Level 2 노드 가져오기
  const level2Group = orgData.children?.find(
    (child) => child.id === "level2-group"
  );
  const level2Nodes = level2Group?.children || [];

  return (
    <>
      {/* PC 버전: 기존 조직도 (lg 이상) */}
      <div
        className="hidden lg:block w-full h-250 lg:h-320 [@media(min-width:1459px)]:h-320 overflow-y-visible"
        ref={chartRef}
      >
        <div className="h-full flex flex-col overflow-x-auto pb-10">
          {/* 루트 노드 */}
          <div className="relative flex flex-col items-center w-full min-w-158 lg:min-w-210 px-20">
            <OrgNode
              node={orgData}
              level={0}
              isExpanded={true}
              onToggle={handleToggle}
              chartWidth={chartWidth}
              expandedNodeId={expandedNodeId}
              selectedDepartment={selectedDepartment}
              orgData={orgData}
            />

            {/* 새로운 연결선 구조 */}
            <div className="relative flex flex-col items-center">
              {/* 1단계: 사업단장에서 내려오는 세로선 (위원회 분기점까지) */}
              <div className="relative">
                <div className="w-0.5 h-42 lg:h-52 bg-gray-400"></div>
                {/* 위원회로 가는 가로선 (우측으로) */}
                <div
                  className="absolute bg-gray-400"
                  style={{
                    width: "140px",
                    height: "2px",
                    top: "50%",
                    left: "2px",
                  }}
                />
                {/* 가로선 끝 변곡점 */}
                <div
                  className="absolute bg-gray-400 rounded-full"
                  style={{
                    width: "6px",
                    height: "6px",
                    top: "51%",
                    left: "140px",
                    transform: "translateY(-50%)",
                  }}
                />
              </div>

              {/* 위원회 박스 (우측에 배치) */}
              <div
                className="absolute flex flex-col items-center"
                style={{
                  left: "638px",
                  top: "30px",
                }}
              >
                <div className="relative flex flex-col gap-2 p-3 lg:p-4 border-2 border-dashed border-gray-300 rounded-2xl bg-gray-400/5">
                  {/* 성과관리위원회 */}
                  <button
                    onClick={() => handleToggle("성과관리위원회")}
                    className={`px-3 lg:px-4 py-2 lg:py-2.5 text-[10px] lg:text-xs font-bold rounded-lg transition-all duration-100 transform hover:scale-105 ${
                      selectedDepartment === "성과관리위원회"
                        ? "bg-[#E91E8C] text-white ring-2 ring-[#E91E8C] ring-offset-1"
                        : "bg-gradient-to-t from-[#34247B] to-[#424242] text-white"
                    }`}
                  >
                    성과관리위원회
                  </button>
                  {/* 자체평가위원회 */}
                  <button
                    onClick={() => handleToggle("자체평가위원회")}
                    className={`px-3 lg:px-4 py-2 lg:py-2.5 text-[10px] lg:text-xs font-bold rounded-lg transition-all duration-100 transform hover:scale-105 ${
                      selectedDepartment === "자체평가위원회"
                        ? "bg-[#E91E8C] text-white ring-2 ring-[#E91E8C] ring-offset-1"
                        : "bg-gradient-to-t from-[#34247B] to-[#424242] text-white"
                    }`}
                  >
                    자체평가위원회
                  </button>
                  {/* 실무추진위원회 */}
                  <button
                    onClick={() => handleToggle("실무추진위원회")}
                    className={`px-3 lg:px-4 py-2 lg:py-2.5 text-[10px] lg:text-xs font-bold rounded-lg transition-all duration-100 transform hover:scale-105 ${
                      selectedDepartment === "실무추진위원회"
                        ? "bg-[#E91E8C] text-white ring-2 ring-[#E91E8C] ring-offset-1"
                        : "bg-gradient-to-t from-[#34247B] to-[#424242] text-white"
                    }`}
                  >
                    실무추진위원회
                  </button>
                </div>
              </div>

              {/* 2단계: 부단장으로 가는 가로선 */}
              <div className="relative">
                {/* 가로선 (좌우로 뻗음) */}
                <div
                  className="absolute bg-gray-400"
                  style={{
                    width: "792px",
                    height: "2px",
                    top: "0",
                    left: "-396px",
                  }}
                />
              </div>

              {/* 부단장 박스들 */}
              <div className="flex justify-center gap-[600px] mt-0">
                {/* 전략기획부단장 */}
                <div className="flex flex-col items-center">
                  <div className="w-0.5 h-6 lg:h-8 bg-gray-400"></div>
                  <OrgNode
                    node={orgData}
                    level={0}
                    isExpanded={true}
                    onToggle={handleToggle}
                    chartWidth={chartWidth}
                    expandedNodeId={expandedNodeId}
                    sub="전략기획부단장"
                    selectedDepartment={selectedDepartment}
                    orgData={orgData}
                  />
                </div>
                {/* 실행운영부단장 */}
                <div className="flex flex-col items-center">
                  <div className="w-0.5 h-6 lg:h-8 bg-gray-400"></div>
                  <OrgNode
                    node={orgData}
                    level={0}
                    isExpanded={true}
                    onToggle={handleToggle}
                    chartWidth={chartWidth}
                    expandedNodeId={expandedNodeId}
                    sub="실행운영부단장"
                    selectedDepartment={selectedDepartment}
                    orgData={orgData}
                  />
                </div>
              </div>
            </div>
            <div className="w-px h-16 lg:h-20 bg-white" />

            <div className="flex items-center absolute bottom-3 lg:bottom-4 right-2 lg:right-3 text-xs lg:text-base">
              <Check className="w-3.5 h-3.5 lg:w-5 lg:h-5 text-[#08A55E] stroke-3 mr-1 lg:mr-2" />
              부서를 클릭하면 직원 목록이 나타납니다
            </div>
          </div>
          {/* 레벨 2: 부서들 */}
          <div className="relative w-full min-w-158 lg:min-w-210 flex-1 py-4 lg:py-6 px-0 lg:px-10 border-3 lg:border-5 border-border/50 rounded-3xl overflow-hidden">
            {renderLevel2Nodes()}
          </div>
        </div>
      </div>

      {/* 조직도 안내 메시지 (PC 버전) - 선택된 부서가 없을 때만 표시 */}
      {!selectedDepartment && (
        <div className="hidden lg:block text-center text-gray-500 mt-8 p-8 bg-gray-50 rounded-lg">
          <p className="text-lg">
            위의 조직도를 클릭하여 상세 직원정보를 확인하세요.
          </p>
        </div>
      )}

      {/* 모바일 버전: 아코디언 스타일 (lg 미만) */}
      <div className="lg:hidden space-y-4">
        {/* RISE 사업단장 - 클릭 가능 */}
        <div className="mb-6">
          <button
            onClick={() => onDepartmentSelect("root")}
            className={`w-full border-2 rounded-lg px-6 py-4 shadow-md text-center transition-all ${
              selectedDepartment === "root"
                ? "bg-[#E91E8C] border-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                : "bg-gradient-to-r from-[#08A55E] to-[#006CAF] border-gray-700 hover:scale-[1.02]"
            }`}
          >
            <h1 className="text-lg font-bold text-white">RISE 사업단장</h1>
          </button>
        </div>

        {/* 부단장 */}
        <div className="mb-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onDepartmentSelect("전략기획부단장")}
              className={`px-3 py-3 text-sm font-bold text-white text-center rounded-lg shadow-md transition-all ${
                selectedDepartment === "전략기획부단장"
                  ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                  : "bg-gradient-to-t from-[#34247B] to-[#424242] hover:scale-105"
              }`}
            >
              전략기획부단장
            </button>
            <button
              onClick={() => onDepartmentSelect("실행운영부단장")}
              className={`px-3 py-3 text-sm font-bold text-white text-center rounded-lg shadow-md transition-all ${
                selectedDepartment === "실행운영부단장"
                  ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                  : "bg-gradient-to-t from-[#34247B] to-[#424242] hover:scale-105"
              }`}
            >
              실행운영부단장
            </button>
          </div>
        </div>

        {/* 위원회 */}
        <div className="mb-6">
          <div className="relative border-2 border-dashed border-gray-300 rounded-lg bg-white shadow-md p-4">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-white border border-gray-300 rounded text-xs font-semibold text-gray-600">
              위원회
            </div>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <button
                onClick={() => onDepartmentSelect("성과관리위원회")}
                className={`px-2 py-3 text-xs font-semibold text-white text-center rounded-lg transition-all ${
                  selectedDepartment === "성과관리위원회"
                    ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                    : "bg-gradient-to-t from-[#34247B] to-[#424242] hover:scale-105"
                }`}
              >
                성과관리위원회
              </button>
              <button
                onClick={() => onDepartmentSelect("자체평가위원회")}
                className={`px-2 py-3 text-xs font-semibold text-white text-center rounded-lg transition-all ${
                  selectedDepartment === "자체평가위원회"
                    ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                    : "bg-gradient-to-t from-[#34247B] to-[#424242] hover:scale-105"
                }`}
              >
                자체평가위원회
              </button>
              <button
                onClick={() => onDepartmentSelect("실무추진위원회")}
                className={`px-2 py-3 text-xs font-semibold text-white text-center rounded-lg transition-all ${
                  selectedDepartment === "실무추진위원회"
                    ? "bg-[#E91E8C] ring-2 ring-[#E91E8C] ring-offset-2"
                    : "bg-gradient-to-t from-[#34247B] to-[#424242] hover:scale-105"
                }`}
              >
                실무추진위원회
              </button>
            </div>
          </div>
        </div>

        {/* 부서 아코디언 */}
        <div className="space-y-3">
          {level2Nodes.map((section) => (
            <div
              key={section.id}
              className="border-2 border-gray-400 rounded-lg bg-white shadow overflow-hidden"
            >
              {/* 섹션 헤더 */}
              <button
                onClick={() => toggleMobileSection(section.id)}
                className="w-full px-4 py-4 bg-[#53488C] flex items-center justify-between hover:bg-[#2D2552] transition-colors"
              >
                <h3 className="text-base font-bold text-white">
                  {section.name}
                </h3>
                <svg
                  className={`w-5 h-5 text-white transition-transform ${
                    expandedMobileSection === section.id ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* 하위 부서 목록 */}
              {expandedMobileSection === section.id && section.children && (
                <div className="border-t-2 border-gray-200 bg-gray-50 p-4">
                  <ul className="space-y-2">
                    {section.children.map((dept) => (
                      <li key={dept.id}>
                        <button
                          onClick={() => onDepartmentSelect(dept.id)}
                          className={`
                            text-sm text-left w-full px-3 py-2 rounded transition-colors
                            ${
                              selectedDepartment === dept.id
                                ? "bg-[#618C48] text-white font-semibold"
                                : "text-gray-700 hover:bg-gray-100"
                            }
                          `}
                        >
                          • {dept.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 조직도 안내 메시지 (모바일 버전) - 선택된 부서가 없을 때만 표시 */}
        {!selectedDepartment && (
          <div className="text-center text-gray-500 mt-8 p-8 bg-gray-50 rounded-lg">
            <p className="text-lg">
              위의 조직도를 클릭하여 상세 직원정보를 확인하세요.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default InteractiveOrgChart;
